import { useEffect, useState } from "react";
import { AiOutlineMinusCircle, AiOutlinePlusCircle } from "react-icons/ai";
import { BiDotsHorizontalRounded } from "react-icons/bi";
import ScrollContainer from "react-indiana-drag-scroll";
import { CartesianGrid, Legend, Line, LineChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import gearBoxImage from "../../Retrofit/Assets/Sizing/additional_transmission_gear.png";
import mechanicsMiddle from "../../Retrofit/Assets/Sizing/additional_transmission_shaft.png";
import mechanicsTail from "../../Retrofit/Assets/Sizing/mechanism_motor.png";
import mechanicsHead from "../../Retrofit/Assets/Sizing/mechanism_universal_833x542.png";

import { useSelector } from "react-redux";
import Button from "../../Common/components/Buttons/Button/Button";
import { calculateSingleAxis } from "../../Common/utils/api/api";
import { selectSizingDynamics, selectSizingGearBox, selectSizingMechanics } from "../store/slices/retrofit";

const LEFT1_NAME = "torque";
const LEFT1_LABEL = "torque";
const LEFT1_COLOR = "#8884d8";
const YAXIS_COLOR = "#000000";
const YAXIS_ORIENTATION = "left";
const LINE_TYPE = "monotone";
const DOMAIN = [0, 1];
// const TICKS = Array.from({ length: 32 }, (_, i) => (i * 0.02).toFixed(2)).map(Number);

const getCollapsableHeaderContent = (length) => {
  const content = [];
  for (let i = 0; i < length - 1; i++) {
    content.push(
      <th className="px-0">
        <div className="flex items-center">
          <span className="text-sm invisible-element w-0">a</span>
          <hr className="bg-gray-500 h-0.1 flex-1" />
          {i === length - 2 && <span className="bg-gray-500 w-0.1 h-3" />}
        </div>
      </th>
    );
  }
  return content;
};

const TableCollapsableHeader = ({ title, length, updateFunction, defaultValue, expand, expandFunction }) => (
  <>
    <th className="font-normal text-left pl-3 pr-0 text-gray-600" key="product-0-header">
      <div className="flex items-center">
        <span className="text-sm whitespace-nowrap">{title}</span>
        <span className="mx-2 flex items-center">
          {length > 1 && expand && (
            <AiOutlineMinusCircle
              className="text-sm cursor-pointer"
              onClick={() => {
                updateFunction(1);
                expandFunction(false);
              }}
            />
          )}
          {length > 1 && !expand && (
            <AiOutlinePlusCircle
              className="text-sm cursor-pointer"
              onClick={() => {
                updateFunction(defaultValue);
                expandFunction(true);
              }}
            />
          )}
        </span>
        {!expand && <BiDotsHorizontalRounded className="text-xs text-gray-600" />}
        <hr className="bg-gray-400 h-0.1 flex-1" />
      </div>
    </th>
    {getCollapsableHeaderContent(length)}
  </>
);

const TableCollapsableHeadersSection = () => {
  return (
    <tr>
      <th className="main py-3 px-1 text-transparent"></th>
      <TableCollapsableHeader
        title="1"
        length="1"
        //updateFunction={setProductLength}
        defaultValue="1"
        expand={true}
      />
      <TableCollapsableHeader
        title="2"
        length="1"
        //updateFunction={setProductLength}
        defaultValue="1"
        expand={true}
      />
      <TableCollapsableHeader
        title="3"
        length="2"
        //updateFunction={setProductLength}
        defaultValue="2"
        expand={true}
      />

      <TableCollapsableHeader
        title="4"
        length="2"
        //updateFunction={setProductLength}
        defaultValue="2"
        expand={true}
      />
    </tr>
  );
};

const TableHeadersSection = () => {
  return (
    <tr>
      <th className="font-normal text-center pl-3 pr-0 text-gray-600 bg-white min-w-[120px]" key="product-0-header">
        <div className="flex items-center">
          <span className="text-sm whitespace-nowrap"></span>
        </div>
      </th>
      <th className="font-normal text-center text-gray-600 bg-[#CBCBCB] border border-gray-400">
        <div className="flex justify-center items-center m-2">
          <span className="text-sm">Application data at the load</span>
        </div>
      </th>
      <th className="font-normal text-center text-gray-600 bg-[#CBCBCB] border border-gray-400" key="product-0-header">
        <div className="flex justify-center items-center m-2">
          <span className="text-sm ">Application data at the machine shaft</span>
        </div>
      </th>
      <th className="font-normal text-center text-gray-600 bg-[#CBCBCB] border border-gray-400" key="product-0-header">
        <div className="flex justify-center items-center m-2">
          <span className="text-sm ">Application data at the gearbox shaft</span>
        </div>
      </th>
      <th className="font-normal text-center text-gray-600 bg-[#CBCBCB] border border-gray-400" key="product-0-header">
        <div className="flex justify-center items-center m-2">
          <span className="text-sm ">Permitted gearbox value</span>
        </div>
      </th>
      <th className="font-normal text-center text-gray-600 bg-[#CBCBCB] border border-gray-400" key="product-0-header">
        <div className="flex justify-center items-center m-2">
          <span className="text-sm ">Application data at the motor shaft</span>
        </div>
      </th>
      <th className="font-normal text-center text-gray-600 bg-[#CBCBCB] border border-gray-400" key="product-0-header">
        <div className="flex justify-center items-center m-2">
          <span className="text-sm ">Permitted motor value</span>
        </div>
      </th>
    </tr>
  );
};

const SubHeadingSection = ({ subheading }) => {
  return (
    <tr className="group hover:outline-2 hover:outline-dashed hover:outline-gray-300 text-sm cursor-pointer">
      <td className="bg-[#666666] text-[#CBCBCB] p-2">{subheading}</td>
      <td className="bg-[#666666] min-w-[120px]"></td>
      <td className="bg-[#666666] min-w-[120px]"></td>
      <td className="bg-[#666666] min-w-[120px]"></td>
      <td className="bg-[#666666] min-w-[120px]"></td>
      <td className="bg-[#666666] min-w-[120px]"></td>
      <td className="bg-[#666666] min-w-[120px]"></td>
    </tr>
  );
};

const DataSection = ({
  heading,
  applicationDataLoad,
  applicationDataMachineShaft,
  applicationDataGearShaft,
  permittedGearBox,
  applicationDataMotorShaft,
  permittedMotor
}) => {
  return (
    <tr className="group hover:outline-2 hover:outline-dashed hover:outline-gray-300 text-sm cursor-pointer">
      <td className="bg-[#A0A4A8] text-[#CBCBCB] border-b  border border-gray-200 pl-2 pt-1">{heading}</td>
      <td className="bg-[#F7F7F7] text-center max-w-[150px] border-b  border border-gray-200">{applicationDataLoad}</td>
      <td className="bg-[#F7F7F7] text-center max-w-[150px] border-b  border border-gray-200">
        {applicationDataMachineShaft}
      </td>
      <td className="bg-[#F7F7F7] text-center max-w-[150px] border-b  border border-gray-200">
        {applicationDataGearShaft}
      </td>
      <td className="bg-[#F7F7F7] text-center max-w-[150px] border-b  border border-gray-200">{permittedGearBox}</td>
      <td className="bg-[#F7F7F7] text-center max-w-[150px] border-b  border border-gray-200">
        {applicationDataMotorShaft}
      </td>
      <td className="bg-[#F7F7F7] text-center max-w-[150px] border-b  border border-gray-200">{permittedMotor}</td>
    </tr>
  );
};

const MOTOR_DATA = {
  ratedSpeed: {
    dimension: "RotationalSpeed",
    unit: "RevolutionsPerMinute",
    value: 180000
  },
  peakTorque: {
    dimension: "Torque",
    unit: "NewtonMeter",
    value: 520
  },
  standstillTorque: {
    dimension: "Torque",
    unit: "NewtonMeter",
    value: 520
  },
  ratedTorqueAtHalfSpeed: {
    dimension: "Torque",
    unit: "NewtonMeter",
    value: 520
  },
  ratedTorqueAtFullSpeed: {
    dimension: "Torque",
    unit: "NewtonMeter",
    value: 520
  },
  inductance: {
    dimension: "Inductance",
    unit: "Henry",
    value: 1e-323
  },
  inertiaOfBrake: {
    dimension: "Inertia",
    unit: "KilogramSquareCentimeter",
    value: 1e-323
  },
  inertiaOfMotor: {
    dimension: "Inertia",
    unit: "KilogramSquareCentimeter",
    value: 1e-323
  },
  numberOfPolePairs: 5,
  resistanceOfWinding: {
    dimension: "ElectricResistance",
    unit: "Ohm",
    value: 1e-323
  },
  torqueConstant: 0.052,
  ratedCurrent: {
    dimension: "ElectricCurrent",
    unit: "Ampere",
    value: 1000
  },
  peakCurrent: {
    dimension: "ElectricCurrent",
    unit: "Ampere",
    value: 1000
  },
  temperatureAmbientMin: {
    dimension: "Temperature",
    unit: "Celsius",
    value: -273.15
  },
  temperatureAmbientMax: {
    dimension: "Temperature",
    unit: "Celsius",
    value: 27000000
  },
  temperatureDerationStart: {
    dimension: "Temperature",
    unit: "Celsius",
    value: 27000000
  },
  temperatureDerationScale: 100,
  operationalAltitudeMin: {
    dimension: "Length",
    unit: "Meter",
    value: -11034
  },
  operationalAltitudeMax: {
    dimension: "Length",
    unit: "Meter",
    value: 8848.86
  },
  altitudeDerationStart: {
    dimension: "Length",
    unit: "Meter",
    value: 8848.86
  },
  altitudeDerationScale: 100,
  motorFiguresBaseSupplyVoltage: {
    dimension: "Voltage",
    unit: "Volt",
    value: 480
  }
};

const DRIVE_DATA = {
  RatedVoltage: {
    dimension: "Voltage",
    unit: "Volt",
    value: 480
  },
  ratedCurrent: {
    dimension: "ElectricCurrent",
    unit: "Ampere",
    value: 1000
  },
  peakCurrent: {
    dimension: "ElectricCurrent",
    unit: "Ampere",
    value: 1000
  },
  temperatureAmbientMin: {
    dimension: "Temperature",
    unit: "Celsius",
    value: -273.15
  },
  temperatureAmbientMax: {
    dimension: "Temperature",
    unit: "Celsius",
    value: 27000000
  },
  temperatureDerationStart: {
    dimension: "Temperature",
    unit: "Celsius",
    value: 27000000
  },
  temperatureDerationScale: 100,
  operationalAltitudeMin: {
    dimension: "Length",
    unit: "Meter",
    value: -11034
  },
  operationalAltitudeMax: {
    dimension: "Length",
    unit: "Meter",
    value: 8848.86
  },
  altitudeDerationStart: {
    dimension: "Length",
    unit: "Meter",
    value: 8848.86
  },
  altitudeDerationScale: 100
};

const TableSection = ({ singleAxis, setSingleAxis }) => {
  const mechanics = useSelector(selectSizingMechanics);
  const dynamics = useSelector(selectSizingDynamics);
  const gearbox = useSelector(selectSizingGearBox);

  const getSingleAxis = async () => {
    const payload = {
      motor: MOTOR_DATA,
      drive: DRIVE_DATA,
      additionalTransmission:
        gearbox.isGearBox === "Additional gearbox"
          ? {
              AdditionalTransmissionType: "Gear",
              efficiency: {
                dimension: "Ratio",
                unit: "Percent",
                value: Number(gearbox.efficiency)
              },
              ratio: Number(gearbox.gearRatio),
              torqueFriction: {
                dimension: "Torque",
                unit: "NewtonMeter",
                value: Number(gearbox.kineticFrictionTorque)
              },
              torqueViscous: {
                dimension: "ViscousTorque",
                unit: "NewtonMeterPer1000RevolutionsPerMinute",
                value: Number(gearbox.mViscous)
              },
              inertiaGear: {
                dimension: "Inertia",
                unit: "KilogramSquareCentimeter",
                value: Number(gearbox.momentOfInertia)
              }
            }
          : gearbox.isGearBox === "Belt drive"
            ? {
                AdditionalTransmissionType: "Belt",
                efficiency: {
                  dimension: "Ratio",
                  unit: "Percent",
                  value: Number(gearbox.beltDriveEfficiency)
                },
                torqueFriction: {
                  dimension: "Torque",
                  unit: "NewtonMeter",
                  value: Number(gearbox.beltDriveKineticFrictionTorque)
                },
                torqueViscous: {
                  dimension: "ViscousTorque",
                  unit: "NewtonMeterPer1000RevolutionsPerMinute",
                  value: Number(gearbox.beltDriveMViscous)
                },
                diameterDriverPulleyRoll: {
                  dimension: "Length",
                  unit: "Meter",
                  value: Number(gearbox.drivePulleyDiameter)
                },
                inertiaDriverPulley: {
                  dimension: "Inertia",
                  unit: "KilogramSquareCentimeter",
                  value: Number(gearbox.drivePulleyMomentOfInertia)
                },
                diameterDrivenPulleyRoll: {
                  dimension: "Length",
                  unit: "Meter",
                  value: Number(gearbox.drivenPulleyDiameter)
                },
                inertiaDrivenPulley: {
                  dimension: "Inertia",
                  unit: "KilogramSquareCentimeter",
                  value: Number(gearbox.drivenPulleyMomentOfInertia)
                },
                massBelt: {
                  dimension: "Mass",
                  unit: "Kilogram",
                  value: Number(gearbox.beltMass)
                },
                inertiaAdditional: {
                  dimension: "Inertia",
                  unit: "KilogramSquareCentimeter",
                  value: Number(gearbox.additionalMomentOfInertia)
                }
              }
            : gearbox.isGearBox === "Coupling"
              ? {
                  AdditionalTransmissionType: "Coupling",
                  efficiency: {
                    dimension: "Ratio",
                    unit: "Percent",
                    value: 0
                  },
                  torqueFriction: {
                    dimension: "Torque",
                    unit: "NewtonMeter",
                    value: 0
                  },
                  torqueViscous: {
                    dimension: "ViscousTorque",
                    unit: "NewtonMeterPer1000RevolutionsPerMinute",
                    value: 0
                  },
                  inertia: {
                    dimension: "Inertia",
                    unit: "KilogramSquareMeter",
                    value: gearbox.couplingMomentOfInertia
                  }
                }
              : undefined,
      mechanism: {
        MechanismType: "Generic",
        jLoad: {
          dimension: "Inertia",
          unit: "KilogramSquareCentimeter",
          value: Number(mechanics.momentOfInertiaLoad)
        },
        feedConstant: {
          dimension: "FeedConstant",
          unit: "MillimeterPerRotation",
          value: Number(mechanics.feedConstant)
        },
        mViscous: {
          dimension: "ViscousTorque",
          unit: "NewtonMeterPer1000RevolutionsPerSecond",
          value: Number(mechanics.viscousFrictionTorque)
        },
        mStatic: {
          dimension: "Torque",
          unit: "NewtonMeter",
          value: Number(mechanics.additionalTorqueConstant)
        },
        mFriction: {
          dimension: "Torque",
          unit: "NewtonMeter",
          value: Number(mechanics.kineticFrictionTorque)
        }
      },
      motionProfile: {
        position: dynamics.pureMotionDesign?.profile?.timeBasedProfile?.segments
          ?.map((segment) => segment.positionValues)
          .flat(),
        velocity: dynamics.pureMotionDesign?.profile?.timeBasedProfile?.segments
          ?.map((segment) => segment.velocityValues)
          .flat(),
        acceleration: dynamics.pureMotionDesign?.profile?.timeBasedProfile?.segments
          ?.map((segment) => segment.accelerationValues)
          .flat(),
        jerk: dynamics.pureMotionDesign?.profile?.timeBasedProfile?.segments
          ?.map((segment) => segment.jerkValues)
          .flat()
      },
      cycleTime: {
        dimension: "Time",
        unit: "Second",
        value: 1
      }
    };
    let singleAxis = await calculateSingleAxis(payload);
    singleAxis = singleAxis?.payload;
    setSingleAxis(singleAxis.result);
  };

  const getUnitMapping = (unit) => {
    switch (unit) {
      case "NewtonMeter":
        return "Nm";
      case "RevolutionsPerSecond":
        return "1/s (Rounds per sec)";
      case "RadPerSquareSecond":
        return "rad/s²";
      case "KilogramSquareMeter":
        return "kgm²";
      default:
        return unit;
    }
  };

  const getValueFromProperty = (property) => {
    const value = singleAxis?.[property]?.value;
    const unit = singleAxis?.[property]?.unit;

    if (value === undefined) {
      return "-";
    }
    // round to 2 decimals
    return value.toFixed(2) + " " + getUnitMapping(unit);
  };

  useEffect(() => {
    getSingleAxis();
  }, []);

  return (
    <div className="flex flex-col bg-white">
      <ScrollContainer hideScrollbars={false}>
        <div className="w-[95%] h-[95%] mx-auto">
          {/* <table cellSpacing={0} className="border-separate min-w-full mb-2 ml-4"> */}
          <table className="min-w-full divide-y divide-gray-200">
            <thead>
              <TableCollapsableHeadersSection />
              <TableHeadersSection />
            </thead>
            <tbody className="border-b border-solid border-gray-200 " key="tbody">
              <SubHeadingSection subheading="Torque" />
              <DataSection
                heading="Maximal torque"
                applicationDataLoad="-"
                applicationDataMachineShaft={getValueFromProperty("atLoadPeakTorque")}
                applicationDataGearShaft={
                  gearbox.isGearBox !== "No additional gear" ? getValueFromProperty("betweenGearboxesPeakTorque") : "-"
                }
                permittedGearBox="-"
                applicationDataMotorShaft={getValueFromProperty("betweenMaingearMotorPeakTorque")}
                permittedMotor="-"
              />
              <DataSection
                heading="Effective torque at effective speed"
                applicationDataLoad="-"
                applicationDataMachineShaft={getValueFromProperty("atLoadRatedTorque")}
                applicationDataGearShaft={
                  gearbox.isGearBox !== "No additional gear" ? getValueFromProperty("betweenGearboxesRatedTorque") : "-"
                }
                permittedGearBox="-"
                applicationDataMotorShaft={getValueFromProperty("betweenMaingearMotorRatedTorque")}
                permittedMotor="-"
              />
              <DataSection
                heading="Effective torque at nominal speed"
                applicationDataLoad="-"
                applicationDataMachineShaft="-"
                applicationDataGearShaft="-"
                permittedGearBox="-"
                applicationDataMotorShaft=""
                permittedMotor=""
              />
              <DataSection
                heading="Quick stop torque"
                applicationDataLoad="-"
                applicationDataMachineShaft="-"
                applicationDataGearShaft="-"
                permittedGearBox="-"
                applicationDataMotorShaft=""
                permittedMotor=""
              />
              <SubHeadingSection subheading="Speed" />
              <DataSection
                heading="Maximal speed"
                applicationDataLoad={
                  dynamics.pureMotionDesign?.profile?.timeBasedProfile?.segments
                    .map((segment) => segment.velocityValues)
                    .flat()
                    .reduce((a, b) => Math.max(a, b))
                    .toFixed(2) + " 1/s (Rounds per sec)"
                }
                applicationDataMachineShaft={getValueFromProperty("atLoadMaxSpeed")}
                applicationDataGearShaft={
                  gearbox.isGearBox !== "No additional gear" ? getValueFromProperty("betweenGearboxesMaxSpeed") : "-"
                }
                permittedGearBox="-"
                applicationDataMotorShaft={getValueFromProperty("betweenMaingearMotorMaxSpeed")}
                permittedMotor="-"
              />
              <DataSection
                heading="Effective speed"
                applicationDataLoad="-"
                applicationDataMachineShaft={getValueFromProperty("atLoadMaxSpeed")}
                applicationDataGearShaft={
                  gearbox.isGearBox !== "No additional gear" ? getValueFromProperty("betweenGearboxesMaxSpeed") : "-"
                }
                permittedGearBox="-"
                applicationDataMotorShaft={getValueFromProperty("betweenMaingearMotorMaxSpeed")}
                permittedMotor="-"
              />
              <SubHeadingSection subheading="Acceleration" />
              <DataSection
                heading="Peak acceleration"
                applicationDataLoad={
                  dynamics.pureMotionDesign?.profile?.timeBasedProfile?.segments
                    .map((segment) => segment.accelerationValues)
                    .flat()
                    .reduce((a, b) => Math.max(a, b))
                    .toFixed(2) + " rad/s²"
                }
                applicationDataMachineShaft={getValueFromProperty("atLoadMaxAngularAcceleration")}
                applicationDataGearShaft={
                  gearbox.isGearBox !== "No additional gear"
                    ? getValueFromProperty("betweenGearboxesMaxAngularAcceleration")
                    : "-"
                }
                permittedGearBox="-"
                applicationDataMotorShaft={getValueFromProperty("betweenMaingearMotorMaxAngularAcceleration")}
                permittedMotor="-"
              />
              <SubHeadingSection subheading="Moment of inertia" />
              <DataSection
                heading="Load inertia"
                applicationDataLoad="-"
                applicationDataMachineShaft={getValueFromProperty("atLoadInertiaLoad")}
                applicationDataGearShaft={
                  gearbox.isGearBox !== "No additional gear" ? getValueFromProperty("betweenGearboxesInertiaLoad") : "-"
                }
                permittedGearBox="-"
                applicationDataMotorShaft={getValueFromProperty("betweenMaingearMotorInertiaLoad")}
                permittedMotor="-"
              />
              <DataSection
                heading="Motor inertia"
                applicationDataLoad="-"
                applicationDataMachineShaft="-"
                applicationDataGearShaft="-"
                permittedGearBox="-"
                applicationDataMotorShaft="-"
                permittedMotor="-"
              />
              <DataSection
                heading="Inertia ratio"
                applicationDataLoad="-"
                applicationDataMachineShaft="-"
                applicationDataGearShaft="-"
                permittedGearBox="-"
                applicationDataMotorShaft="-"
                permittedMotor="-"
              />
            </tbody>
          </table>
        </div>
      </ScrollContainer>
    </div>
  );
};

const ImageSection = () => {
  return (
    <div className="pl-3 flex flex-col space-y-3 ml-28 mr-28 mt-3 mb-3">
      <div className="flex">
        <div className="relative flex-1 border-2 border-transparent">
          <img src={mechanicsHead} alt="linearAxis" className="w-full" />
          <div className="absolute top-1/2 left transform -translate-x-1/2 -translate-y-1/2 bg-primary w-5 h-5  flex justify-center items-center">
            <p className="text-white">1</p>
          </div>
        </div>
        <div className="relative flex-1 border-y-2 border-transparent">
          <img src={gearBoxImage} alt="linearAxis" className="w-full" />
          <div className="absolute top-1/2 left transform -translate-x-1/2 -translate-y-1/2 bg-primary w-5 h-5  flex justify-center items-center">
            <p className="text-white">2</p>
          </div>
        </div>
        <div className="relative flex-1 border-y-2 border-transparent">
          <img src={mechanicsMiddle} alt="linearAxis" className="w-full" />
          <div className="absolute top-1/2 left transform -translate-x-1/2 -translate-y-1/2 bg-primary w-5 h-5  flex justify-center items-center">
            <p className="text-white">3</p>
          </div>
        </div>
        <div className="relative flex-1 border-y-2 border-transparent">
          <img src={mechanicsTail} alt="linearAxis" className="w-full" />
          <div className="absolute top-1/2 left transform -translate-x-1/2 -translate-y-1/2 bg-primary w-5 h-5  flex justify-center items-center">
            <p className="text-white">4</p>
          </div>
        </div>
      </div>
    </div>
  );
};

const ButtonSection = ({ selectedButton, setSelectedButton }) => {
  return (
    <div className="flex">
      <div className="border-2 border-gray-300 inline-block rounded-l-md rounded-r-md h-6">
        <div className="flex">
          <Button
            className={`px-4 pb-5 border-r-2 border-gray-300 text-sm rounded-l-md h-5 ${
              selectedButton === "Result" ? "bg-primary rounded-l-[3px]" : ""
            }`}
            onClick={() => {
              setSelectedButton("Result");
            }}
          >
            Result
          </Button>
          <Button
            className={`px-4 pb-5 border-r-2 border-gray-300  text-sm h-5 ${
              selectedButton === "Torque over time" ? "bg-primary" : ""
            }`}
            onClick={() => {
              setSelectedButton("Torque over time");
            }}
          >
            Torque over time
          </Button>
          <Button
            className={`px-4 pb-5 border-r-2 border-gray-300  text-sm h-5 ${
              selectedButton === "Torque over speed" ? "bg-primary" : ""
            }`}
            onClick={() => {
              setSelectedButton("Torque over speed");
            }}
          >
            Torque over speed
          </Button>
          <Button
            className={`px-4 pb-5  text-sm h-5 ${
              selectedButton === "Torque over gearbox factor" ? "bg-primary rounded-r-[3px]" : ""
            }`}
            onClick={() => {
              setSelectedButton("Torque over gearbox factor");
            }}
          >
            Torque over gearbox factor
          </Button>
        </div>
      </div>
    </div>
  );
};

const TopSection = ({ selectedButton, setSelectedButton }) => {
  return (
    <div className="flex justify-center">
      <ButtonSection selectedButton={selectedButton} setSelectedButton={setSelectedButton} />
    </div>
  );
};

const ApplySection = ({ enable, handleContinue }) => {
  return (
    <div className="flex p-2 justify-center space-x-5 absolute bottom-0 bg-white border-t left-0 w-full drop-shadow-xl hover:drop-shadow-2xl transition-all duration-75 z-10">
      <qds-button
        id="naviguator-button-continue"
        {...(!enable ? { disabled: "" } : {})}
        onClick={() => {
          handleContinue();
        }}
        text="Cancel and discard changes"
      />
      <qds-button
        id="naviguator-button-continue"
        {...(!enable ? { disabled: "" } : {})}
        importance="emphasized"
        onClick={() => {
          handleContinue();
        }}
        text="Proceed to Bundle Selection"
      />
    </div>
  );
};

const CustomYAxisTick = ({ x, y, payload, color }) => {
  return (
    <text x={x} y={y} fill={color} fontSize={12} textAnchor="end" dy={0.02}>
      {payload.value}
    </text>
  );
};

const formatLegendLabel = (value) => {
  switch (value) {
    case "Mmax":
      return (
        <span className="text-xs">
          M max
          <br />
          (115 V AC, 1 phase)
        </span>
      );
    case "Meff":
      return (
        <span className="text-xs">
          M eff
          <br />
          (103.5 V AC, 1 phase)
        </span>
      );
    default:
      return value;
  }
};

const ChartSection = ({ singleAxis }) => {
  const [graphData, setGraphData] = useState(null);
  const dynamics = useSelector(selectSizingDynamics);
  const formatValue = (value, name) => {
    const formattedValue = Number(value).toFixed(2);
    return parseFloat(formattedValue).toString();
  };

  useEffect(() => {
    // build graphData
    /*
    the data should follow this pattern
    const data = [
  { x: 0, torque: 0 },
  { x: 300, torque: 0.1 },
  { x: 600, torque: 0.2},
  { x: 900, torque: 0.3 },
  { x: 1200, torque: 0.4},
  { x: 1500, torque: 0.5 },
  { x: 1800, torque: 0.6 },
  { x: 2100, torque: 0.5 },
  { x: 2400, torque: 0.4 }
];
    torque values are in singleAxis.result.mbynTorque.values
    x values should be created based on max time value in this array
    dynamics.pureMotionDesign?.profile?.timeBasedProfile?.segments[?].xValues
    */

    if (singleAxis) {
      const maxXValues = Math.max(
        ...dynamics.pureMotionDesign?.profile?.timeBasedProfile?.segments?.map((segment) =>
          Math.max(...segment.xValues)
        )
      );
      const torqueValues = singleAxis?.mbynSpeed?.values;

      // build x values which is an array which has the same size of the array of torque values
      const arrayLength = torqueValues?.length || 0;
      const xValues =
        arrayLength > 0 && maxXValues > 0
          ? Array.from({ length: arrayLength }, (_, i) => (i * maxXValues) / arrayLength)
          : [];

      // build data array
      const data = xValues.map((x, index) => {
        return {
          x: x,
          torque: torqueValues[index]
        };
      });
      setGraphData(data);
      console.log(data);
    }
  }, [singleAxis]);

  return (
    <div className="mt-5 mr-4">
      <ResponsiveContainer width="100%" height={400}>
        <LineChart data={graphData} margin={{ top: 5, right: 0, left: 10, bottom: 5 }}>
          <CartesianGrid strokeDasharray="3 3" />
          <XAxis
            dataKey="x"
            fontSize={12}
            label={{
              value: "Speed at the motor shaft in 1.min-1",
              position: "insideBottom",
              offset: 0,
              style: { textAnchor: "middle", fontWeight: "bold" }
            }}
            tickFormatter={formatValue}
          />

          {/* Multiple Y Axes */}

          <YAxis
            yAxisId={LEFT1_NAME}
            tick={<CustomYAxisTick color={LEFT1_COLOR} />}
            orientation={YAXIS_ORIENTATION}
            stroke={YAXIS_COLOR}
            allowDecimals={true}
            domain={DOMAIN}
            // ticks={TICKS}
            tickFormatter={formatValue}
            interval={0}
            label={{
              value: "Torque at the motor shaft in N.m",
              angle: -90,
              position: "insideLeft",
              offset: 0,
              style: { textAnchor: "middle", fontWeight: "bold" }
            }}
          />
          <Tooltip />
          <Legend
            layout="vertical"
            verticalAlign="middle"
            align="left"
            wrapperStyle={{
              border: "1px solid #ccc",
              padding: "10px",
              margin: "-15px",
              backgroundColor: "#f9f9f9"
            }}
            formatter={formatLegendLabel}
            //content={CustomLegend}
            //content={<RenderLegend />}
          />

          <Line
            yAxisId={LEFT1_NAME}
            type={LINE_TYPE}
            dataKey={LEFT1_LABEL}
            stroke={LEFT1_COLOR}
            dot={false}
            tickFormatter={formatValue}
          />
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
};

const SizingResults = () => {
  const [selectedButton, setSelectedButton] = useState("Result");
  const [singleAxis, setSingleAxis] = useState(null);

  return (
    <div className="flex-col justify-center">
      <TopSection selectedButton={selectedButton} setSelectedButton={setSelectedButton} />
      {selectedButton === "Result" && (
        <>
          <ImageSection />
          <TableSection singleAxis={singleAxis} setSingleAxis={setSingleAxis} />
        </>
      )}
      {selectedButton !== "Result" && <ChartSection singleAxis={singleAxis} />}
      <ApplySection />
    </div>
  );
};

export default SizingResults;
